/**
 * Moving between applications.
 *
 * The rail navigates within where you are; this goes somewhere else. Planned applications appear
 * here too, disabled — a person should be able to see what is coming without hunting for it, and
 * a disabled entry that says why is kinder than an absence that says nothing.
 */
import { useState } from 'react';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import GridViewRounded from '@mui/icons-material/GridViewRounded';
import {
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';

import { keys, platform } from '@/api/endpoints';
import { appIdForPath } from '@/platform/registry';

export function AppSwitcher(): JSX.Element {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const { pathname } = useLocation();
  const { data } = useQuery({
    queryKey: keys.platformApps(),
    queryFn: platform.apps,
    staleTime: 5 * 60_000,
  });

  const apps = data ?? [];
  const currentId = appIdForPath(pathname);
  const current = apps.find((app) => app.id === currentId);
  const label = current?.name ?? 'Strategic Planning Hub';

  return (
    <>
      <Button
        onClick={(event) => setAnchor(event.currentTarget)}
        startIcon={<GridViewRounded />}
        endIcon={<ExpandMoreRounded />}
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        aria-label={`Switch application, currently ${label}`}
        sx={{ color: 'text.primary', textTransform: 'none', fontWeight: 600 }}
      >
        {label}
      </Button>

      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem component={Link} to="/" onClick={() => setAnchor(null)}>
          <ListItemIcon>
            <GridViewRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Home" secondary="All applications" />
        </MenuItem>
        <Divider />

        {apps.map((app) =>
          app.canOpen ? (
            <MenuItem
              key={app.id}
              component={Link}
              to={app.href}
              selected={app.id === currentId}
              onClick={() => setAnchor(null)}
            >
              <ListItemText primary={app.name} secondary={app.description} />
            </MenuItem>
          ) : (
            <MenuItem key={app.id} disabled>
              <ListItemText
                primary={app.name}
                secondary={app.status === 'Planned' ? 'Coming soon' : 'No access'}
              />
            </MenuItem>
          ),
        )}

        {apps.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2">No applications available.</Typography>
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
}
